/**
 * Types partagés pour le module Base de Connaissances
 * Fusion des concepts Subjects (collections) et Media (contenus)
 */

// ============================================================================
// Collections (ex-Subjects)
// ============================================================================

export type CollectionSource = 'manual' | 'wordpress'

export type Collection = {
  id: number
  title: string
  source: CollectionSource
  source_ref?: string | null
  user_id: number
  /** Langue déclarée du corpus KB (API subjects.content_language). */
  content_language?: string | null
  /** Nombre de contenus dans cette collection (calculé côté client) */
  contentCount?: number
}

export type CollectionFormData = {
  title: string
  /** ISO kb lang ou '' pour laisser le défaut backend (NULL) */
  content_language?: string | null
}

// ============================================================================
// Contenus (ex-Media)
// ============================================================================

export type ContentStatus = 
  | 'uploaded' 
  | 'transcribing' 
  | 'transcribed' 
  | 'indexing' 
  | 'indexed' 
  | 'error'

export type Content = {
  id: number
  title: string
  status: ContentStatus
  subject_id?: number | null
  course_id?: string | null
  course_title?: string | null
  created_at: string
  last_indexed_at?: string | null
  chunking_strategy?: string | null
}

export type ContentDetail = {
  id: number
  title: string
  status: ContentStatus
  categories: string[]
  course_id?: string | null
  course_title?: string | null
  mime_type?: string | null
  filename?: string | null
  size_bytes?: number | null
  created_at: string
  transcription_language?: string | null
  transcription_length: number
  transcription_preview: string
  chunks: number
  embeddings_total: number
  embeddings_by_category: Record<string, number>
  chunking_strategy?: string | null
  // Collection
  subject_id?: number | null
  subject_label?: string | null
}

// ============================================================================
// Configuration UI
// ============================================================================

export type StatusConfig = {
  icon: string
  label: string
  color: 'amber' | 'blue' | 'sky' | 'purple' | 'emerald' | 'red' | 'zinc'
  dotColor: string
  animate: boolean
}

export const STATUS_CONFIG: Record<ContentStatus, StatusConfig> = {
  uploaded: { icon: '⏳', label: 'En attente', color: 'amber', dotColor: 'bg-amber-500', animate: false },
  transcribing: { icon: '🔄', label: 'Transcription', color: 'blue', dotColor: 'bg-blue-500', animate: true },
  transcribed: { icon: '✓', label: 'Transcrit', color: 'sky', dotColor: 'bg-sky-500', animate: false },
  indexing: { icon: '🔄', label: 'Indexation', color: 'purple', dotColor: 'bg-purple-500', animate: true },
  indexed: { icon: '✅', label: 'Prêt', color: 'emerald', dotColor: 'bg-emerald-500', animate: false },
  error: { icon: '❌', label: 'Erreur', color: 'red', dotColor: 'bg-red-500', animate: false },
}

export const STATUS_PALETTE: Record<ContentStatus, string> = {
  uploaded: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
  transcribing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
  transcribed: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
  indexing: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200',
  indexed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
}

// ============================================================================
// Statistiques
// ============================================================================

export type KnowledgeStats = {
  // Collections
  totalCollections: number
  manualCollections: number
  wordpressCollections: number
  collectionsWithContent: number
  
  // Contenus
  totalContents: number
  indexedContents: number
  pendingContents: number
  errorContents: number
  orphanContents: number
  
  // Métriques
  avgContentsPerCollection: number
  topCollection: { id: number; title: string; count: number } | null
}

// ============================================================================
// Filtres
// ============================================================================

export type KnowledgeFilters = {
  search: string
  status: ContentStatus | 'all'
  collectionId: number | 'orphan' | null
}

export const DEFAULT_FILTERS: KnowledgeFilters = {
  search: '',
  status: 'all',
  collectionId: null,
}

// ============================================================================
// WordPress Sync Schedules
// ============================================================================

export type SyncContentType = 'page' | 'post' | 'sfwd-lessons' | 'sfwd-topic' | 'product'
export type SyncFrequency = '6h' | 'daily' | 'weekly'
export type SyncStatus = 'pending' | 'running' | 'success' | 'partial' | 'failed' | 'cancelled'
export type SyncTriggerType = 'scheduled' | 'manual'

export const CONTENT_TYPE_LABELS: Record<SyncContentType, string> = {
  'page': 'Pages',
  'post': 'Articles',
  'sfwd-lessons': 'Leçons',
  'sfwd-topic': 'Chapitres',
  'product': 'Produits',
}

export const CONTENT_TYPE_ICONS: Record<SyncContentType, string> = {
  'page': '📄',
  'post': '📝',
  'sfwd-lessons': '🎓',
  'sfwd-topic': '📚',
  'product': '🛒',
}

export const FREQUENCY_LABELS: Record<SyncFrequency, string> = {
  '6h': 'Toutes les 6h',
  'daily': 'Quotidien',
  'weekly': 'Hebdomadaire',
}

export const SYNC_STATUS_CONFIG: Record<SyncStatus, { icon: string; label: string; color: string }> = {
  pending: { icon: '⏳', label: 'En attente', color: 'text-amber-500' },
  running: { icon: '🔄', label: 'En cours', color: 'text-blue-500' },
  success: { icon: '✅', label: 'Succès', color: 'text-emerald-500' },
  partial: { icon: '⚠️', label: 'Partiel', color: 'text-orange-500' },
  failed: { icon: '❌', label: 'Échec', color: 'text-red-500' },
  cancelled: { icon: '🚫', label: 'Annulé', color: 'text-zinc-500' },
}

export type SyncSchedule = {
  public_id: string
  name: string
  enabled: boolean
  content_type: SyncContentType
  content_type_label: string
  target_collection_id: number | null
  target_collection_name: string | null
  frequency: SyncFrequency
  preferred_hour: number
  preferred_day: number
  last_sync_at: string | null
  last_sync_status: SyncStatus | null
  next_sync_at: string | null
  created_at: string
}

export type SyncScheduleCreate = {
  name: string
  content_type: SyncContentType
  target_collection_id?: number | null
  frequency: SyncFrequency
  preferred_hour: number
  preferred_day: number
  enabled: boolean
}

export type SyncScheduleUpdate = Partial<Omit<SyncScheduleCreate, 'content_type'>>

export type SyncHistory = {
  public_id: string
  schedule_public_id: string
  schedule_name: string
  started_at: string
  finished_at: string | null
  duration_seconds: number | null
  status: SyncStatus
  trigger_type: SyncTriggerType
  items_found: number
  items_new: number
  items_updated: number
  items_skipped: number
  items_failed: number
  error_message: string | null
}

export type SyncHistoryDetail = SyncHistory & {
  details_json: Record<string, unknown> | null
}
